import { useRef } from "react";
import { Navigate } from "react-router-dom";

import { AppLayout } from "@/components/shared/AppLayout";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PublicWizardLayout } from "@/components/shared/PublicWizardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { TenantSwitcherProvider } from "@/contexts/TenantSwitcherContext";
import { NotaryRequestsPage } from "@/pages/notary/requests-page";
import { NotaryRequestDetailPage } from "@/pages/notary/request-detail-page";
import { PlatformRequestsPage } from "@/pages/platform/requests-page";
import { PlatformNotarialRequestDetailPage } from "@/pages/platform/platform-notarial-request-detail-page";
import { UserNewRequestWizardPage } from "@/pages/requests/user-new-request-wizard-page";
import { UserRequestsPage } from "@/pages/requests/user-requests-page";
import { UserRequestDetailPage } from "@/pages/requests/user-request-detail-page";
import { practisesAnyProfession } from "@/lib/is-professional";

export function RequestsListRoute(): JSX.Element {
  const { user } = useAuth();
  const isPlatform = user?.platformRole === "root" || user?.platformRole === "root_admin";

  /*
    ANY profession takes the professional view. This read the two that existed
    when it was written, so a third was routed to the CONSUMER experience — a
    traductor would have been shown the screens for buying work rather than
    doing it. The page is still named for notaries; it has served both
    professions for a while.
  */
  if (practisesAnyProfession(user)) {
    return <NotaryRequestsPage />;
  }
  if (isPlatform) {
    return <PlatformRequestsPage />;
  }
  return <UserRequestsPage />;
}

export function RequestsDetailRoute(): JSX.Element {
  const { user } = useAuth();
  const isPlatform = user?.platformRole === "root" || user?.platformRole === "root_admin";

  /*
    ANY profession takes the professional view. This read the two that existed
    when it was written, so a third was routed to the CONSUMER experience — a
    traductor would have been shown the screens for buying work rather than
    doing it. The page is still named for notaries; it has served both
    professions for a while.
  */
  if (practisesAnyProfession(user)) {
    return <NotaryRequestDetailPage />;
  }
  if (isPlatform) {
    return <PlatformNotarialRequestDetailPage />;
  }
  return <UserRequestDetailPage />;
}

/**
 * Consumer certificate wizard. Reachable both anonymously (guest checkout) and authenticated.
 *
 * - Anonymous visitors: render the wizard inside <PublicWizardLayout> (minimal chrome,
 *   no sidebar, no auth-dependent hooks). The auth modal at the summary step handles
 *   sign-up / sign-in before payment.
 * - Authenticated consumers: render inside the full <AppLayout> (sidebar + header).
 *   The wizard route deliberately bypasses <ProtectedRoute>'s email-verify / account-kind /
 *   name redirect gates — those would interrupt the purchase flow. Email verification is
 *   deferred to a post-purchase prompt per the guest-checkout design (CommitMind task 2ab59fb).
 * - Authenticated professionals or platform users: redirect to /requests; they don't
 *   use the consumer wizard.
 */
type ResolvedShell = "public" | "consumer" | "redirectProfessional";

export function RequestsNewWizardRoute(): JSX.Element {
  const { user, isLoading } = useAuth();

  /**
   * Lock the shell decision at first render. Auth flips DURING the wizard's lifetime
   * (e.g. the visitor signs up via the AuthModal at the summary step) MUST NOT swap
   * the rendered tree from PublicWizardLayout to AppLayout — that would unmount the
   * entire wizard subtree mid-submit, destroy the in-flight Promise chain that fires
   * the dLocal redirect, and flash an empty wizard at the user. The locked choice
   * stays in effect for the lifetime of THIS mount; on the next fresh navigation to
   * /requests/new the visitor (now authenticated) re-mounts and gets AppLayout.
   */
  const lockedShellRef = useRef<ResolvedShell | null>(null);

  if (lockedShellRef.current === null) {
    if (isLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <LoadingLogo size="lg" />
        </div>
      );
    }
    if (!user) {
      lockedShellRef.current = "public";
    } else {
      const isPlatform = user.platformRole === "root" || user.platformRole === "root_admin";
      lockedShellRef.current =
        practisesAnyProfession(user) || isPlatform ? "redirectProfessional" : "consumer";
    }
  }

  if (lockedShellRef.current === "redirectProfessional") {
    return <Navigate to="/requests" replace />;
  }

  if (lockedShellRef.current === "public") {
    return (
      <PublicWizardLayout>
        <UserNewRequestWizardPage />
      </PublicWizardLayout>
    );
  }

  return (
    <TenantSwitcherProvider>
      <AppLayout>
        <UserNewRequestWizardPage />
      </AppLayout>
    </TenantSwitcherProvider>
  );
}
